import { FC, useEffect, useRef, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui';
import { step5Schema, type Step5Data as FormData } from '@/validations';
import api from '@/utils/api';

interface Step4ContentTypesProps {
  initialData?: {
    contentTypes?: Array<{ id: number; category: string }>;
    dailyPostCount?: number;
  };
  onNext: (data: FormData) => void;
  onBack?: () => void;
  isSubmitting?: boolean;
  skipReset?: boolean;
}

export const Step4ContentTypes: FC<Step4ContentTypesProps> = ({
  initialData,
  onNext,
  onBack,
  isSubmitting = false,
  skipReset = false,
}) => {
  // State for dynamic categories
  const [contentTypeOptions, setContentTypeOptions] = useState<Array<{ id: number; category: string }>>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm<FormData>({
    resolver: zodResolver(step5Schema),
    defaultValues: {
      contentTypeIds: initialData?.contentTypes?.map((c) => c.id) || [],
      dailyPostCount: initialData?.dailyPostCount ?? 3,
    },
  });

  const dailyPostCount = watch('dailyPostCount');

  // Fetch categories from API on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await api.get('/company/post-categories');
        const categories = response.data || [];
        setContentTypeOptions(categories);

        // If no initial contentTypes provided, pre-select all categories
        if (!initialData?.contentTypes || initialData.contentTypes.length === 0) {
          setValue('contentTypeIds', categories.map((c: any) => c.id));
        }
      } catch (error) {
        console.error('Error fetching content type categories:', error);
        // Fallback to empty array if API fails
        setContentTypeOptions([]);
      } finally {
        setLoadingCategories(false);
      }
    };
    fetchCategories();
  }, [initialData?.contentTypes, setValue]);

  // Reset form when initialData changes, but only if it's actually different
  const prevInitialDataRef = useRef(initialData);
  useEffect(() => {
    if (skipReset) return;
    const prevIds = prevInitialDataRef.current?.contentTypes?.map(c => c.id).sort().join(',') || '';
    const currentIds = initialData?.contentTypes?.map(c => c.id).sort().join(',') || '';
    const prevCount = prevInitialDataRef.current?.dailyPostCount ?? 3;
    const currentCount = initialData?.dailyPostCount ?? 3;
    if (prevIds === currentIds && prevCount === currentCount && prevInitialDataRef.current) return;
    prevInitialDataRef.current = initialData;

    // When resetting, if contentTypes is empty, use all available categories
    const contentTypeIdsToUse = initialData?.contentTypes && initialData.contentTypes.length > 0
      ? initialData.contentTypes.map(c => c.id)
      : contentTypeOptions.length > 0 ? contentTypeOptions.map(c => c.id) : [];

    reset({
      contentTypeIds: contentTypeIdsToUse,
      dailyPostCount: initialData?.dailyPostCount ?? 3,
    });
  }, [initialData, reset, skipReset, contentTypeOptions]);

  return (
    <form onSubmit={handleSubmit(onNext)} className="space-y-6">
      {/* Header */}
      {onBack && (
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Content Generation</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Select the types of content you want to automate and set your daily post count
          </p>
        </div>
      )}

      {/* Post Types */}
      <div className="space-y-1.5">
        <label className="text-sm font-medium text-slate-700">
          Post Types <span className="text-red-500">*</span>
        </label>
        <Controller
          name="contentTypeIds"
          control={control}
          render={({ field }) => {
            const value = field.value || [];
            return (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3">
                {loadingCategories ? (
                  <div className="col-span-2 py-4 flex items-center justify-center">
                    <div className="animate-spin h-5 w-5 border-2 border-slate-300 border-t-slate-900 rounded-full" />
                    <span className="ml-2 text-sm text-slate-500">Loading categories...</span>
                  </div>
                ) : contentTypeOptions.length === 0 ? (
                  <div className="col-span-2 py-4 text-center text-sm text-slate-500">
                    No categories available
                  </div>
                ) : (
                  contentTypeOptions.map((option) => (
                    <label
                      key={option.id}
                      className={`flex items-center px-3 py-2.5 border rounded-lg cursor-pointer transition-all ${value.includes(option.id)
                        ? 'border-slate-900 bg-slate-50'
                        : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                        }`}
                    >
                      <input
                        type="checkbox"
                        value={option.id}
                        checked={value.includes(option.id)}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          const newValue = checked
                            ? [...value, option.id]
                            : value.filter((v) => v !== option.id);
                          field.onChange(newValue);
                        }}
                        className="w-4 h-4 text-slate-900 border-slate-300 rounded focus:ring-slate-900/10"
                      />
                      <span className="ml-2.5 text-sm text-slate-900">{option.category}</span>
                    </label>
                  ))
                )}
              </div>
            );
          }}
        />
        {errors.contentTypeIds && (
          <p className="text-xs text-red-600">{errors.contentTypeIds.message}</p>
        )}
      </div>

      {/* Daily Post Count */}
      <div className="space-y-2 pt-4 border-t border-slate-200">
        <label className="text-sm font-medium text-slate-700">
          Daily Post Generation <span className="text-red-500">*</span>
        </label>
        <p className="text-xs text-slate-500">
          Choose how many posts to generate each day
        </p>
        <div className="pt-2">
          <div className="inline-flex rounded-lg border border-slate-300 overflow-hidden">
            {[
              { value: 1, label: '1 Post', icon: '📄' },
              { value: 2, label: '2 Posts', icon: '📄📄' },
              { value: 3, label: '3 Posts', icon: '📄📄📄' },
            ].map((option, index) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setValue('dailyPostCount', option.value)}
                className={`
                  relative px-6 py-3 text-sm font-medium transition-all
                  ${index > 0 ? 'border-l border-slate-300' : ''}
                  ${dailyPostCount === option.value
                    ? 'bg-slate-900 text-white z-10'
                    : 'bg-white text-slate-700 hover:bg-slate-50'
                  }
                `}
              >
                <div className="flex flex-col items-center gap-1">
                  <span className="text-lg">{option.icon}</span>
                  <span>{option.label}</span>
                </div>
              </button>
            ))}
          </div>
          {errors.dailyPostCount && (
            <p className="text-xs text-red-600 mt-2">{errors.dailyPostCount.message}</p>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-6 mt-6 border-t border-slate-200 justify-end">
        {onBack && (
          <Button type="button" variant="secondary" onClick={onBack}>
            Back
          </Button>
        )}
        <Button type="submit" variant="primary" isLoading={isSubmitting}>
          {isSubmitting ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </form>
  );
};
